// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ga from 'app/common/lib/ga';
import i18n from 'app/common/lib/i18n';
import PropTypes from 'prop-types';
import Toast from 'app/common/react/ui/Toast';
import type { Dispatch } from 'redux';
import withAuthenticatedAction from 'app/common/react/components/limitedLogin/withAuthenticatedAction';
import withShowToast from 'app/common/lib/react/withShowToast';
import { compose } from 'redux';
import { track } from 'app/common/lib/react/analytics';
import { Box, Button, Pulsar, Touchable } from 'gestalt';
import { follow, unfollow } from 'app/common/redux/actions/board/follow';

const followGACategory = 'board_follow';
const unfollowGACategory = 'board_unfollow';

type BoardFollowButtonProps = {
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  authAction: *,
  boardId: string,
  boardUrl: string,
  boardImage: string,
  boardName: string,
  follow: (id: string) => void,
  hideAllToasts: () => void,
  isFollowed: boolean,
  ownerId: string,
  showToast: (Toast: Object) => void,
  size?: 'sm' | 'md' | 'lg',
  showPulsar: boolean,
  unfollow: (id: string) => void,
  isBoardLandingPage?: boolean,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
};

type BoardFollowButtonDefaultProps = void;

// $FlowFixMe Flow v0.104.0 upgrade supression.
export class BoardFollowButton extends PureComponent<BoardFollowButtonProps, *> {
  static propTypes = {
    authAction: PropTypes.func.isRequired,
    boardId: PropTypes.string.isRequired,
    boardUrl: PropTypes.string.isRequired,
    boardImage: PropTypes.string.isRequired,
    boardName: PropTypes.string.isRequired,
    follow: PropTypes.func.isRequired,
    hideAllToasts: PropTypes.func.isRequired,
    isFollowed: PropTypes.bool.isRequired,
    ownerId: PropTypes.string.isRequired,
    showToast: PropTypes.func.isRequired,
    size: PropTypes.string,
    unfollow: PropTypes.func.isRequired,
    viewParameter: PropTypes.number,
  };

  static defaultProps: BoardFollowButtonDefaultProps;

  followUnfollow = () => {
    const { boardId, follow, hideAllToasts, isFollowed, showToast, unfollow } = this.props;

    if (isFollowed) {
      unfollow(boardId);
    } else {
      follow(boardId);
      const { boardImage, boardUrl, boardName } = this.props;
      hideAllToasts();
      showToast(({ onHide }) => (
        <Toast
          onHide={onHide}
          text={[
            i18n._(
              'Now following',
              'text that comes before a board/user name in toast on homefeed',
            ),
            boardName,
          ]}
          href={boardUrl}
          imageUrl={boardImage}
        />
      ));
    }
  };

  logBoardFollowClick = (isBoardFollowed: boolean) => {
    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.FLOWED_BOARD,
      element: isBoardFollowed
        ? PCONST.thrift.ElementType.BOARD_UNFOLLOW
        : PCONST.thrift.ElementType.BOARD_FOLLOW,
      objectId: this.props.boardId,
      view: this.props.isBoardLandingPage
        ? PCONST.thrift.ViewType.BOARD
        : PCONST.thrift.ViewType.USER,
      viewParameter: this.props.viewParameter,
    });
  };

  handleFollow = ({ event }: { event: SyntheticMouseEvent<> }) => {
    event.preventDefault();
    event.stopPropagation();

    const { authAction, isFollowed } = this.props;
    const GACategory = isFollowed ? unfollowGACategory : followGACategory;

    authAction(this.followUnfollow)();

    this.logBoardFollowClick(isFollowed);
    ga.trackEvent(GACategory, PCONST.GA_ACTION_CLICK);
  };

  handlePulsarClick = (event: any) => {
    this.setState({ isPaused: true });
    this.handleFollow(event);
  };

  render() {
    const { isFollowed, showPulsar, size } = this.props;
    const buttonText = isFollowed ? i18n._('Following') : i18n._('Follow');

    return (
      <Box
        position="relative"
        display="flex"
        data-test-id={`board-${!isFollowed ? 'follow' : 'unfollow'}-button`}
      >
        <Button
          color={isFollowed ? 'gray' : 'red'}
          inline
          onClick={this.handleFollow}
          size={size}
          text={buttonText}
        />
        {showPulsar && (
          <Touchable onTouch={this.handlePulsarClick}>
            <Box
              position="absolute"
              display="inlineBlock"
              dangerouslySetInlineStyle={{ __style: { marginLeft: -86, marginTop: -28 } }}
            >
              <Pulsar paused={isFollowed} />
            </Box>
          </Touchable>
        )}
      </Box>
    );
  }
}

const mapStateToProps = (state, { boardId, isFollowed }) => {
  const board = state.boards.content[boardId];
  return { isFollowed: board ? board.followed_by_me : isFollowed };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  follow: (id) => dispatch(follow(id)),
  unfollow: (id) => dispatch(unfollow(id)),
});

export default compose(
  // $FlowFixMe: add connect<Props, OwnProps, , , _, _>() typing to remove this error
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withAuthenticatedAction,
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  withShowToast,
)(BoardFollowButton);
